/**
 * Storage Service - Manejo de localStorage para tokens JWT y configuración mínima
 *
 * IMPORTANTE: Este archivo SOLO almacena datos de sesión (4 strings).
 * Todos los datos de la aplicación (emails, calendario, adjuntos, config Mailgun)
 * están en Cloudflare D1/R2, NO en el navegador.
 */

const STORAGE_KEYS = {
  WORKER_URL: 'mocaccino:worker_url',
  USER_EMAIL: 'mocaccino:user_email',
  ACCESS_TOKEN: 'mocaccino:access_token',
  REFRESH_TOKEN: 'mocaccino:refresh_token',
} as const;

// ==========================================
// TIPOS
// ==========================================

export interface CloudflareConfig {
  worker_url: string;
  user_email: string;
}

export interface StoredTokens {
  access_token: string | null;
  refresh_token: string | null;
}

export interface AuthState {
  logged_in: boolean;
  email: string | null;
  needs_migration: boolean;
  has_tokens: boolean;
}

// ==========================================
// FUNCIONES DE CONFIGURACIÓN
// ==========================================

/**
 * Obtiene la configuración de Cloudflare (URL del worker y email del usuario)
 */
export function getCloudflareConfig(): CloudflareConfig {
  return {
    worker_url: localStorage.getItem(STORAGE_KEYS.WORKER_URL) || '',
    user_email: localStorage.getItem(STORAGE_KEYS.USER_EMAIL) || '',
  };
}

/**
 * Guarda la configuración de Cloudflare
 */
export function saveCloudflareConfig(config: Partial<CloudflareConfig>): void {
  if (config.worker_url !== undefined) {
    localStorage.setItem(STORAGE_KEYS.WORKER_URL, config.worker_url);
  }
  if (config.user_email !== undefined) {
    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, config.user_email);
  }
}

// ==========================================
// FUNCIONES DE TOKENS
// ==========================================

/**
 * Obtiene los tokens almacenados
 */
export function getStoredTokens(): StoredTokens {
  return {
    access_token: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
    refresh_token: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
  };
}

/**
 * Almacena los tokens JWT
 */
export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
}

/**
 * Limpia los tokens (logout)
 */
export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
}

// ==========================================
// FUNCIONES DE AUTENTICACIÓN
// ==========================================

/**
 * Decodifica un JWT para obtener el payload (sin verificar firma)
 */
function decodeJwtPayload(token: string): { exp?: number; sub?: string } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Verifica si un token JWT ha expirado
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;

  // Considerar expirado si faltan menos de 60 segundos
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + 60;
}

/**
 * Obtiene el estado de autenticación actual
 */
export function getAuthState(): AuthState {
  const tokens = getStoredTokens();
  const config = getCloudflareConfig();

  const hasValidToken = tokens.access_token && !isTokenExpired(tokens.access_token);
  const hasRefreshToken = !!tokens.refresh_token;

  return {
    logged_in: hasValidToken || hasRefreshToken,
    email: config.user_email || null,
    needs_migration: false, // La migración se maneja en el servidor
    has_tokens: hasValidToken || hasRefreshToken,
  };
}

// ==========================================
// FUNCIONES DE LIMPIEZA
// ==========================================

/**
 * Limpia todo el almacenamiento de la aplicación (logout completo)
 */
export function clearAllStorage(): void {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
}
